import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { QUARTERS, fiscalQuarterMonths, fiscalYearLabel, fiscalYearOf } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import {
  EmptyState,
  ForbiddenState,
  ServerErrorState,
} from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { scopeQuery, useCanEdit, useCanView, useSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import {
  useDepartments,
  useHeadcountPlans,
  useSetHeadcountPlan,
  type HeadcountPlanRow,
} from '../api/organisation-api';
import styles from './HeadcountPlanPage.module.css';
import sheet from '../../../components/SheetTable.module.css';

/**
 * ORG-04 — the headcount plan. Per org unit, per quarter.
 *
 * The forward-looking half of goal 3: not "who is open now" but "what will I need
 * to fill". Planned and approved are typed; **filled is computed** from the seats
 * that exist right now (non-negotiable #10), so the grid cannot drift out of step
 * with the org tree the way a stored figure would.
 *
 * O2 was answered in S28 (DECISIONS-LOG S25–S28 Q15): the fiscal year starts in July, so Q1 is
 * July–September and the grid opens on the fiscal year today is in. The
 * grid stores a year and a quarter number, so answering it changed the column
 * headings and nothing else.
 */

export function HeadcountPlanPage(): ReactNode {
  const canView = useCanView('headcount_plan');
  const canEdit = useCanEdit('headcount_plan');
  const navigate = useNavigate();
  const scope = useSession((state) => state.scope);
  const showToast = useToasts((state) => state.show);
  // `useNow` returns a timestamp; reading `Date.now()` during render is impure
  // and oxlint refuses it.
  const now = useNow();

  const [year, setYear] = useState(() => fiscalYearOf(new Date(now).getFullYear(), new Date(now).getMonth() + 1));

  const departments = useDepartments();
  const plans = useHeadcountPlans({ fiscalYear: year, ...scopeQuery(scope) });
  const setPlan = useSetHeadcountPlan();

  if (!canView) {
    return <ForbiddenState resource="headcount plans" contact="Your admin" />;
  }

  if (plans.isError) {
    return <ServerErrorState onRetry={() => void plans.refetch()} />;
  }

  const rows = plans.data ?? [];
  const units = unitsFor(rows, departments.data ?? [], scope.kind === 'all' ? null : scope.id);

  async function save(
    unit: PlanUnit,
    quarter: number,
    planned: number,
    approved: number,
  ): Promise<void> {
    try {
      await setPlan.mutateAsync({
        ...(unit.departmentId ? { departmentId: unit.departmentId } : { ventureId: unit.ventureId }),
        fiscalYear: year,
        quarter,
        plannedHeadcount: planned,
        approvedHeadcount: approved,
      });
    } catch (cause) {
      showToast({
        tone: 'danger',
        message: 'That plan was not saved',
        detail: errorMessage(cause),
      });
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Headcount plan</h1>
          <p className={styles.lead}>
            How many seats each unit expects to need, quarter by quarter. Filled is counted from the
            seats that exist today — it is never typed in.
          </p>
        </div>

        <div className={styles.yearPicker}>
          <Button
            variant="ghost"
            icon={ChevronLeft}
            aria-label="Previous year"
            onClick={() => setYear((current: number) => current - 1)}
          />
          <span className={styles.year}>{fiscalYearLabel(year)}</span>
          <Button
            variant="ghost"
            icon={ChevronRight}
            aria-label="Next year"
            onClick={() => setYear((current: number) => current + 1)}
          />
        </div>
      </header>

      {/* A grid you type into, not a card (DECISIONS-LOG 2026-10-06): no frame, table lines like every other table. */}
      {/* The placeholder is another year's or venture's plan: shown in this grid it reads as this year's, and a
          blur would save it here (D90). This year's numbers have never been on screen, so a skeleton is allowed. */}
      {plans.isPending || plans.isPlaceholderData ? (
        <SkeletonLines count={6} />
      ) : units.length === 0 ? (
        <EmptyState
          isInset
          title="No units to plan for"
          body="Add a department in the org tree, and its four quarters appear here ready to fill in."
          action={
            <Button variant="solid" onClick={() => void navigate({ to: '/organisation' })}>
              Open the org tree
            </Button>
          }
        />
      ) : (
        <div className={styles.scroller}>
          {/* No card around the grid (DECISIONS-LOG 2026-10-06); the spreadsheet table draws its lines and header row. */}
          {/* Keyed by year and scope so every cell starts again from the plan now on screen (D90). */}
          <table key={`${year}:${scope.kind === 'all' ? 'all' : scope.id}`} className={sheet.sheet}>
            <caption className={styles.caption}>
              Planned, approved and filled headcount for {fiscalYearLabel(year)}
            </caption>
            <thead>
              <tr>
                <th scope="col" className={styles.unitHead}>
                  Unit
                </th>
                {QUARTERS.map((quarter) => (
                  <th key={quarter} scope="col" className={styles.quarterHead}>
                    Q{quarter} <span className={styles.quarterMonths}>{fiscalQuarterMonths(quarter)}</span>
                    {/* Two numbers in a cell with nothing naming them is two
                        numbers nobody can read. The pair is labelled once, here,
                        rather than repeated 32 times down the grid. */}
                    <span className={styles.quarterSub}>
                      <span>Planned</span>
                      <span>Approved</span>
                    </span>
                  </th>
                ))}
                <th scope="col" className={styles.quarterHead}>
                  Filled
                  <span className={styles.quarterSub}>
                    <span>Today</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => (
                <tr key={unit.key}>
                  <th scope="row" className={styles.unitCell}>
                    <span className={styles.unitName}>{unit.name}</span>
                    <span className={styles.unitMeta}>{unit.ventureName}</span>
                  </th>

                  {QUARTERS.map((quarter) => {
                    const plan = unit.byQuarter.get(quarter);
                    return (
                      <td key={quarter} className={styles.cell}>
                        <QuarterCell
                          plan={plan}
                          canEdit={canEdit}
                          onSave={(planned, approved) =>
                            void save(unit, quarter, planned, approved)
                          }
                        />
                      </td>
                    );
                  })}

                  <td className={styles.cell}>
                    <span className={styles.filled}>{formatNumber(unit.filledHeadcount)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * One quarter's cell — planned over approved.
 *
 * Saves on blur rather than on a button. 06-qol §3 permits inline editing on safe
 * fields, and a plan number has no side effects: nothing downstream fires because
 * somebody typed a 4. A grid with 32 save buttons would be the alternative.
 */
function QuarterCell({
  plan,
  canEdit,
  onSave,
}: {
  readonly plan: HeadcountPlanRow | undefined;
  readonly canEdit: boolean;
  onSave(planned: number, approved: number): void;
}): ReactNode {
  const [planned, setPlanned] = useState(String(plan?.plannedHeadcount ?? 0));
  const [approved, setApproved] = useState(String(plan?.approvedHeadcount ?? 0));
  const [isEdited, setIsEdited] = useState(false);

  if (!canEdit) {
    return (
      <span className={styles.readOnly}>
        {formatNumber(plan?.plannedHeadcount ?? 0)}
        <span className={styles.approvedRead}>{formatNumber(plan?.approvedHeadcount ?? 0)}</span>
      </span>
    );
  }

  function commit(): void {
    // Clicking in and out saves nothing: only a number somebody typed is a plan (D90).
    if (!isEdited) return;
    setIsEdited(false);
    const p = clamp(planned);
    const a = Math.min(clamp(approved), p);
    setPlanned(String(p));
    setApproved(String(a));
    if (p !== (plan?.plannedHeadcount ?? 0) || a !== (plan?.approvedHeadcount ?? 0)) {
      onSave(p, a);
    }
  }

  return (
    <span className={styles.pair}>
      <Input
        aria-label="Planned"
        value={planned}
        isNumeric
        inputMode="numeric"
        onChange={(event) => {
          setPlanned(event.target.value);
          setIsEdited(true);
        }}
        onBlur={commit}
      />
      <Input
        aria-label="Approved"
        value={approved}
        isNumeric
        inputMode="numeric"
        onChange={(event) => {
          setApproved(event.target.value);
          setIsEdited(true);
        }}
        onBlur={commit}
      />
    </span>
  );
}

function clamp(input: string): number {
  const value = Number.parseInt(input, 10);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(value, 9999);
}

interface PlanUnit {
  readonly key: string;
  readonly name: string;
  readonly ventureName: string;
  readonly ventureId: string;
  readonly departmentId: string | null;
  readonly byQuarter: ReadonlyMap<number, HeadcountPlanRow>;
  readonly filledHeadcount: number;
}

/**
 * One row per department, whether or not a plan exists for it yet.
 *
 * A grid that only lists units somebody has already planned for is a grid where
 * the unit you forgot is invisible — which is the exact failure this screen exists
 * to prevent.
 */
function unitsFor(
  rows: readonly HeadcountPlanRow[],
  departments: readonly { id: string; name: string; ventureId: string }[],
  scopedUnitId: string | null,
): readonly PlanUnit[] {
  const byDepartment = new Map<string, HeadcountPlanRow[]>();
  for (const row of rows) {
    if (row.departmentId === null) continue;
    const list = byDepartment.get(row.departmentId);
    if (list) list.push(row);
    else byDepartment.set(row.departmentId, [row]);
  }

  const ventureNames = new Map(rows.map((row) => [row.ventureId, row.ventureName]));

  return departments
    .filter((d) => scopedUnitId === null || d.id === scopedUnitId || d.ventureId === scopedUnitId)
    .map((department) => {
      const plans = byDepartment.get(department.id) ?? [];
      return {
        key: department.id,
        name: department.name,
        ventureName: ventureNames.get(department.ventureId) ?? '',
        ventureId: department.ventureId,
        departmentId: department.id,
        byQuarter: new Map(plans.map((plan) => [plan.quarter, plan])),
        filledHeadcount: plans[0]?.filledHeadcount ?? 0,
      };
    });
}
